// react and js
import PropTypes from "prop-types";
import * as Yup from "yup";
import { React, useState, useRef, useEffect, useContext } from "react";
import { useFormik, FormikProvider } from "formik";
import { Trans, useTranslation } from "react-i18next";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import backendRequest from "../../../components/BackendRequest";
import NewPlanItemTickData from "../../../components/NewPlanItemTickData";

// hooks
import appContext from "../../../hooks/appContext";

// layouts

// pages

// theme
import {
  B3Tiny,
  ModalActionsLoadingButton,
} from "../../../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function RequestBackendAction(
  setSubmitting,
  setError,
  setSuccess,
  setWarning,
  setReturnMessage,
  postRequestUpdate,
  SubmitRequestContent
) {
  if (
    localStorage.getItem("myReauthentication") === "false" &&
    navigator.onLine === true
  ) {
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");

    backendRequest(
      process.env.REACT_APP_URL_API_DASHBOARD,
      SubmitRequestContent
    )
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("message");

        if (checkKey === true) {
          if (contentFromBackend.message === "success") {
            setSuccess(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
            postRequestUpdate(750);
          } else {
            setWarning(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
          }
        } else {
          setWarning(true);
          combineReturnMessage(contentFromBackend, setReturnMessage);
        }
        setSubmitting(false);
      })
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
          setError(true);
          setSubmitting(false);
        }
      });
  }
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleDistributorPortalReactivateMt5TickDataSubscription({
  distributor,
  dashboardId,
  distributionPlans,
  currentPlanId,
  currentPlanProp,
  categories, 
  sponsored,
  prorataDiscount,
  paymentMethod,
}) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleDistributorPortalReactivateMt5TickDataSubscription.propTypes = {
    distributor: PropTypes.any.isRequired,
    dashboardId: PropTypes.any.isRequired,
    distributionPlans: PropTypes.any.isRequired,
    currentPlanId: PropTypes.any.isRequired,
    currentPlanProp: PropTypes.any.isRequired,
    categories: PropTypes.any.isRequired,
    sponsored: PropTypes.any.isRequired,
    prorataDiscount: PropTypes.any.isRequired,
    paymentMethod: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const wrapperRef = useRef(null);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const postRequestUpdate = (autoClose) => {
    setTimeout(() => {
      handleClose();
      setStateStorage((previousState) => ({
        ...previousState,
        widgetUpdateDistributorPortalManageTickDataDistributionCustomer: true,
      }));
      setError(false);
      setSuccess(false);
      setWarning(false);
    }, autoClose);
  };

  let deactivatedNewPlan = 0;

  let FullDeactivatedPlanStructure = null;

  distributionPlans.forEach((distrubutionPlan) => {
    if (distrubutionPlan.plan_id === currentPlanId) {
      deactivatedNewPlan = currentPlanId;
    }
  });

  distributionPlans.forEach((distrubutionPlan) => {
    if (distrubutionPlan.plan_id === deactivatedNewPlan) {
      FullDeactivatedPlanStructure = distrubutionPlan;
    }
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setError(false);
        setWarning(false);
        setSuccess(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, {
      passive: false,
      capture: false,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const myValidationScheme = Yup.object().shape({});

  const formik = useFormik({
    initialValues: {},
    validationSchema: myValidationScheme,
    onSubmit: (values, { setSubmitting }) => {
      const SubmitRequestContent = {
        request_type: "dashboard",
        route_info: "distributor_portal",
        widget: "mt5_tick_data_customers",
        operation: "reactivate_mt5_tick_data_subscription",
        client: {
          dashboard_access_token:
            stateStorage.userInformation.answer.dashboard_access_token,
        },
        admin: {
          selected_distributor_id: distributor,
        },
        permission: {
          selected_dashboard_id: dashboardId,
        },
      };

      RequestBackendAction(
        setSubmitting,
        setError,
        setSuccess,
        setWarning,
        setReturnMessage,
        postRequestUpdate,
        SubmitRequestContent
      );
    },
  });

  const { isSubmitting, handleSubmit } = formik;

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const [scroll, setScroll] = useState("paper");

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };
  const handleClickClose = () => {
    setOpen(false);
  };

  let discountShown = "";
  if(paymentMethod.toLowerCase() === "paddle")
  {
    discountShown = t("distributorPortal.discount.netPricePercent", { percent: ((parseFloat(prorataDiscount)/parseFloat(FullDeactivatedPlanStructure.plan_fixed_price_value)) * 100).toFixed(1) });
  }
  else
  {
    discountShown = t("distributorPortal.discount.amount", { amount: prorataDiscount });
  }

  return (stateStorage.userInformation.account_settings.config_data.config_data_distributor.manage_payment_actions === "1" ?
    <>
      <B3Tiny
        variant="outlined"
        onClick={handleClickOpen("paper")}
        fullWidth
        sx={{ mb: 1 }}
      >
        {t("distributorPortal.tickData.actions.reactivatePlan")}
      </B3Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">
                {t("distributorPortal.tickData.reactivateDialog.title")}
              </Typography>

              <IconButton
                onClick={handleClickClose}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={25}
                  minHeight={25}
                  color="#2E2E2E"
                />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                }}
              >
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {currentPlanProp.payment_method !== "sponsored" ? (
                    <Typography variant="contentMiddle">
                      {t("distributorPortal.reactivate.body")}
                    </Typography>
                  ) : null}

                  <Box sx={{ pt: 1 }}>
                    <NewPlanItemTickData
                      planInformation={FullDeactivatedPlanStructure}
                      categories={categories}
                      sponsored={sponsored}
                      reviseAction="reactivate"
                      isStopPossible={Boolean(false)}
                    />
                  </Box>

                  <Typography variant="contentSmall">
                    <Trans i18nKey="distributorPortal.reactivate.discountNote" values={{ discount: discountShown }}>
                      <sup>*</sup> {"{{discount}}"} discount on the first payment due to an already paid cycle. The reactivation will initiate a new billing cycle starting from today.
                    </Trans>
                  </Typography>
                </Stack>
              </DialogContent>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                />

                <ModalActionsLoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                >
                  {t("distributorPortal.reactivate.requestApproval")}
                </ModalActionsLoadingButton>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </> : null
  );
}
